import { z } from 'zod';
import { userRoleSchema } from './auth.js';
import { deviceProfileSchema, deviceStatusSchema, deviceSummarySchema, deviceWaitingSchema, updateChannelSchema, } from './device.js';
/**
 * Расширенная карточка устройства для Admin Panel. Секреты, refresh-токены и
 * приватные ключи здесь отсутствуют по определению: сервер их не отдаёт никому.
 * IP и User-Agent хранятся только как хэш (DEVICE_ENROLLMENT.md).
 */
export const adminDeviceSchema = deviceSummarySchema.extend({
    lastIpHash: z.string().nullable(),
    lastUserAgentHash: z.string().nullable(),
    activeSessions: z.number().int().nonnegative(),
    approvedBy: z.string().nullable(),
    lastError: z.string().nullable(),
    hasPublicKey: z.boolean(),
    /** Активен ли код привязки прямо сейчас (для pending-устройств). */
    enrollmentCode: z.string().nullable(),
    /**
     * Профили, привязанные к устройству. Владелец идёт первым; на устройстве с
     * несколькими профилями клиент показывает экран выбора при запуске.
     */
    profiles: z.array(deviceProfileSchema).default([]),
});
export const adminDeviceListResponseSchema = z.object({
    devices: z.array(adminDeviceSchema),
    counts: z.object({
        pending: z.number().int().nonnegative(),
        approved: z.number().int().nonnegative(),
        blocked: z.number().int().nonnegative(),
        revoked: z.number().int().nonnegative(),
    }),
    /** Режим ожидания устройства — вместе со списком: раздел рисует его одним запросом. */
    waiting: deviceWaitingSchema.default({ active: false, expiresAt: null }),
});
export const adminDeviceQuerySchema = z.object({
    status: deviceStatusSchema.optional(),
    platform: z.enum(['web', 'android-tv']).optional(),
});
export const deviceAccessUpdateSchema = z
    .object({
    userId: z.string().max(64).nullable().optional(),
    groups: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    channel: updateChannelSchema.optional(),
    label: z.string().trim().min(1).max(80).optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'нужно указать хотя бы одно поле');
export const adminUserSchema = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    /** Эмодзи для кружка профиля; `null` — клиент рисует инициал. */
    avatar: z.string().max(8).nullable().default(null),
    /** Загруженная картинка профиля; `null` — её нет. */
    avatarUrl: z.string().max(200).nullable().default(null),
    role: userRoleSchema,
    groups: z.array(z.string()),
    disabled: z.boolean(),
    createdAt: z.string(),
    lastLoginAt: z.string().nullable(),
    deviceCount: z.number().int().nonnegative(),
    activeSessions: z.number().int().nonnegative(),
});
export const adminUserListResponseSchema = z.object({ users: z.array(adminUserSchema) });
export const createUserRequestSchema = z.object({
    username: z
        .string()
        .trim()
        .min(3)
        .max(64)
        .regex(/^[a-zA-Z0-9._-]+$/),
    password: z.string().min(10).max(256),
    displayName: z.string().trim().max(80).optional(),
    avatar: z.string().trim().max(8).nullable().optional(),
    role: userRoleSchema.default('user'),
    groups: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
});
export const updateUserRequestSchema = z
    .object({
    /**
     * Логин. Меняется теми же правилами, что и при создании: под ним человек
     * входит, и «Ivan Petrov» в этом поле означал бы аккаунт, в который не
     * войти.
     */
    username: z
        .string()
        .trim()
        .min(3)
        .max(64)
        .regex(/^[a-zA-Z0-9._-]+$/)
        .optional(),
    displayName: z.string().trim().max(80).optional(),
    avatar: z.string().trim().max(8).nullable().optional(),
    role: userRoleSchema.optional(),
    groups: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    disabled: z.boolean().optional(),
    password: z.string().min(10).max(256).optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'нужно указать хотя бы одно поле');
/**
 * Неизменяемая запись аудита. Пишется для каждого действия, меняющего доступ,
 * конфигурацию или релизы — из Admin Panel и из Telegram-бота одинаково.
 */
export const auditEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    actorType: z.enum(['user', 'telegram', 'system', 'anonymous']),
    actorId: z.string().nullable(),
    actorLabel: z.string(),
    action: z.string(),
    targetType: z.string(),
    targetId: z.string().nullable(),
    result: z.enum(['ok', 'denied', 'error']),
    /** Только безопасные поля: без токенов, паролей и upstream URL. */
    details: z.record(z.string(), z.unknown()),
});
export const auditListResponseSchema = z.object({
    entries: z.array(auditEntrySchema),
    total: z.number().int().nonnegative(),
});
/** Сводка состояния для Admin Panel и Telegram `/status`. */
export const adminStatusResponseSchema = z.object({
    version: z.string(),
    uptimeSeconds: z.number().nonnegative(),
    catalogMode: z.enum(['tmdb', 'demo']),
    devices: z.object({
        pending: z.number().int().nonnegative(),
        approved: z.number().int().nonnegative(),
        blocked: z.number().int().nonnegative(),
        revoked: z.number().int().nonnegative(),
    }),
    users: z.number().int().nonnegative(),
    activeSessions: z.number().int().nonnegative(),
    activePlaybackSessions: z.number().int().nonnegative(),
    activeTranscodes: z.number().int().nonnegative(),
    settingsVersion: z.number().int().nonnegative(),
    plugins: z.object({
        loaded: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
    }),
    library: z.object({
        enabled: z.boolean(),
        items: z.number().int().nonnegative(),
        lastScanAt: z.string().nullable(),
    }),
    hardware: z.object({
        ffmpegAvailable: z.boolean(),
        vaapiUserspaceReady: z.boolean(),
        renderNodePresent: z.boolean(),
        /** Проверено ли реальное аппаратное ускорение на этом хосте. */
        hardwareVerified: z.boolean(),
        detail: z.string(),
    }),
    telegramBot: z.object({ enabled: z.boolean(), detail: z.string() }),
});
/**
 * Карточка тайтла для панели «Монитор»: всплывает при наведении на название.
 *
 * Отдельным запросом, а не полем в опросе: панель опрашивается раз в пять
 * секунд, и таскать описания десятков тайтлов в каждом ответе незачем — они
 * нужны только тому, на чьё название навели курсор.
 */
export const monitorTitleCardSchema = z.object({
    contentId: z.string(),
    title: z.string(),
    /** Год выхода строкой; у телеканала — группа. */
    subtitle: z.string().nullable(),
    posterUrl: z.string().nullable(),
    /** `null`, если описания нет или витрина сейчас недоступна. */
    overview: z.string().nullable(),
});
//# sourceMappingURL=admin.js.map